import { t } from './i18n';

/**
 * The API answers in English. Messages a user can actually see are mapped to
 * locale keys here, so an error reads in the chosen UI language; anything not
 * listed (admin-only or developer-facing) passes through unchanged.
 */
const MESSAGES: Record<string, string> = {
  'Incorrect username or password.': 'serverText.incorrectLogin',
  'Too many requests. Please slow down.': 'serverText.tooManyRequests',
  'That code has expired. Sign in again to get a new one.': 'serverText.codeExpired',
  'Too many incorrect codes. Sign in again to get a new one.': 'serverText.codeTooMany',
  'That code is not correct.': 'serverText.codeWrong',
  'This link is invalid or has expired.': 'serverText.linkInvalid',
  'This link is invalid, expired or revoked.': 'serverText.shareInvalid',
  'Your account is no longer active.': 'serverText.accountInactive',
  'This account is no longer active.': 'serverText.thisAccountInactive',
  'This reset link is invalid or has already been used.': 'serverText.resetUsed',
  'This reset link has expired.': 'serverText.resetExpired',
  'Your current password is not correct.': 'serverText.currentPasswordWrong',
  'Not found': 'serverText.notFound',
  'Not available': 'serverText.notAvailable',
  'Not available.': 'serverText.notAvailable',
  'Not authenticated': 'serverText.notAuthenticated',
  'Internal server error': 'serverText.internalError',
  'Invalid request': 'serverText.invalidRequest',
  'Your basket is empty.': 'serverText.basketEmpty',
  'Folders cannot be added to the basket.': 'serverText.foldersNotInBasket',
  'This share is too large to download as a single archive.': 'serverText.shareTooLarge',
  'Share link not found.': 'serverText.shareNotFound',
  'Please sign in to raise this kind of ticket.': 'serverText.signInForTicket',
  'An email address is required so we can reply.': 'serverText.emailRequired',
  'No email address on file.': 'serverText.noEmailOnFile',
  'Could not render this image': 'serverText.cannotRenderImage',
  'Could not render this format': 'serverText.cannotRenderFormat',
  'Could not render a cutting for this image': 'serverText.cannotRenderCutting',
  'Cutting is only available for JPEG and PNG images': 'serverText.cuttingOnlyJpgPng',
  'This asset type cannot be converted': 'serverText.cannotConvert',
  'This asset type has no resized formats': 'serverText.noResizedFormats',
  'Preset not found.': 'serverText.presetNotFound',
  'You cannot edit this preset.': 'serverText.cannotEditPreset',
  'You cannot delete this preset.': 'serverText.cannotDeletePreset',
  'Asset not found.': 'serverText.assetNotFound',
  'Account not found.': 'serverText.accountNotFound',
  'This file is not a valid ZIP archive.': 'serverText.zipInvalid',
  'The ZIP archive contains no files.': 'serverText.zipEmpty',
  'The ZIP archive contains unsafe file paths.': 'serverText.zipUnsafe',
  'This folder is empty.': 'serverText.folderEmpty',
  'This folder is too large to download as one ZIP.': 'serverText.folderZipTooLarge',
};

export function translateServerText(msg: string): string {
  const key = MESSAGES[msg];
  if (key) return t(key);
  const big = /^File too large \(max (\d+) MB\)\.$/.exec(msg);
  if (big) return t('serverText.fileTooLarge', { mb: big[1] ?? '' });
  if (/^You do not have '.+' permission on this item\.$/.test(msg)) return t('serverText.noPermission');
  const many = /^The ZIP archive has too many entries \(max (\d+)\)\.$/.exec(msg);
  if (many) return t('serverText.zipTooManyEntries', { max: many[1] ?? '' });
  const huge = /^The ZIP archive is too large when unpacked \(max (\d+) GB\)\.$/.exec(msg);
  if (huge) return t('serverText.zipTooLarge', { max: huge[1] ?? '' });
  const taken = /^an? (?:asset|folder) named (.+) already exists here$/.exec(msg) ?? /^an item already exists at .*\/([^/]+)$/.exec(msg);
  if (taken) return t('serverText.nameTaken', { name: taken[1] ?? '' });
  if (msg.startsWith('path too long')) return t('serverText.pathTooLong');
  if (msg === 'cannot move a folder into its own subtree' || msg === 'cannot move an item into itself') return t('serverText.cannotMoveIntoItself');
  return msg;
}

/** Download formats come from the server with English labels; key them by id. */
export function formatLabel(f: { id: string; label: string; description: string }) {
  const known = f.id === 'web' || f.id === 'print' || f.id === 'office';
  return known
    ? { label: t(`downloadFormat.${f.id}`), description: t(`downloadFormat.${f.id}Desc`) }
    : { label: f.label, description: f.description };
}
